import type { Dispatch, SetStateAction } from 'react';
import {
  ELEMENT_KINDS,
  designElementLabel,
  designSourceLabel,
  elementKindLabel,
  fmtCoord,
  isRoundElement,
  type GeoDesignElement,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { IconPencil, IconTrash } from '@shared/ui/icons';
import {
  CellActions,
  CellInput,
  CellSelect,
  Check,
  DataTable,
  IconButton,
  Muted,
  NumInput,
  TableWrap,
  ToneRow,
} from '@widgets/GeoSchemes';
import { PAGE, rowValid } from './constants';

interface Props {
  list: GeoDesignElement[];
  shown: GeoDesignElement[];
  limit: number;
  setLimit: Dispatch<SetStateAction<number>>;
  readOnly: boolean;
  editId: string | null;
  row: GeoDesignElement | null;
  setRow: Dispatch<SetStateAction<GeoDesignElement | null>>;
  hToContour: boolean;
  setHToContour: (c: boolean) => void;
  startEdit: (el: GeoDesignElement) => void;
  commitRow: () => void;
  cancelRow: () => void;
  onDelete: (id: string) => void;
}

export function ElementsTable({
  list,
  shown,
  limit,
  setLimit,
  readOnly,
  editId,
  row,
  setRow,
  hToContour,
  setHToContour,
  startEdit,
  commitRow,
  cancelRow,
  onDelete,
}: Props) {
  if (list.length === 0) {
    return (
      <Muted>Элементов нет — загрузите каталог, извлеките из листа или добавьте вручную.</Muted>
    );
  }

  return (
    <>
      <TableWrap $maxHeight={460}>
        <DataTable>
          <thead>
            <tr>
              <th>Метка</th>
              <th>Вид</th>
              <th className="num">X, м</th>
              <th className="num">Y, м</th>
              <th className="num">H, м</th>
              <th className="num">d, мм</th>
              <th>Источник</th>
              <th aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {shown.map((el) =>
              editId === el.id && row ? (
                <ToneRow key={el.id} $tone="warning">
                  <td>
                    <CellInput
                      value={row.label}
                      onChange={(e) => setRow({ ...row, label: e.target.value })}
                      aria-label="Метка"
                      aria-invalid={row.label.trim() === ''}
                      autoFocus
                    />
                  </td>
                  <td>
                    <CellSelect
                      value={row.kind}
                      aria-label="Вид"
                      onChange={(e) => {
                        const k = ELEMENT_KINDS.find((x) => x.value === e.target.value);
                        if (k) setRow({ ...row, kind: k.value });
                      }}
                    >
                      {ELEMENT_KINDS.map((k) => (
                        <option key={k.value} value={k.value}>
                          {k.label}
                        </option>
                      ))}
                    </CellSelect>
                  </td>
                  <td>
                    <NumInput
                      variant="cell"
                      value={row.x}
                      aria-label="X, м"
                      onValue={(v) => setRow({ ...row, x: v ?? Number.NaN })}
                    />
                  </td>
                  <td>
                    <NumInput
                      variant="cell"
                      value={row.y}
                      aria-label="Y, м"
                      onValue={(v) => setRow({ ...row, y: v ?? Number.NaN })}
                    />
                  </td>
                  <td>
                    <NumInput
                      variant="cell"
                      value={row.h}
                      aria-label="H, м"
                      onValue={(v) => setRow({ ...row, h: v })}
                    />
                    {row.contour && !isRoundElement(row) && (
                      <Check checked={hToContour} onChange={setHToContour}>
                        всем углам {row.contour}
                      </Check>
                    )}
                  </td>
                  <td>
                    <NumInput
                      variant="cell"
                      value={row.size_mm}
                      aria-label="d, мм"
                      onValue={(v) => setRow({ ...row, size_mm: v })}
                    />
                  </td>
                  <td>{designSourceLabel(row.source)}</td>
                  <td>
                    <CellActions>
                      <Button
                        type="button"
                        $size="sm"
                        onClick={commitRow}
                        disabled={!rowValid(row)}
                      >
                        Готово
                      </Button>
                      <Button type="button" $size="sm" $variant="ghost" onClick={cancelRow}>
                        Отмена
                      </Button>
                    </CellActions>
                  </td>
                </ToneRow>
              ) : (
                <tr key={el.id}>
                  <td>{designElementLabel(el) || '—'}</td>
                  <td>
                    {elementKindLabel(el.kind)}
                    {isRoundElement(el) ? ` · ⌀ ${fmtCoord((el.size_mm ?? 0) / 1000)} м` : ''}
                    {el.mark ? ` · ${el.mark}` : ''}
                  </td>
                  <td className="num">{fmtCoord(el.x)}</td>
                  <td className="num">{fmtCoord(el.y)}</td>
                  <td className="num">{fmtCoord(el.h)}</td>
                  <td className="num">{el.size_mm ?? '—'}</td>
                  <td>{designSourceLabel(el.source)}</td>
                  <td>
                    {!readOnly && (
                      <CellActions>
                        <IconButton
                          type="button"
                          aria-label={`Править ${el.label}`}
                          onClick={() => startEdit(el)}
                          disabled={editId != null}
                        >
                          <IconPencil size={15} />
                        </IconButton>
                        <IconButton
                          type="button"
                          $danger
                          aria-label={`Удалить ${el.label}`}
                          onClick={() => onDelete(el.id)}
                          disabled={editId != null}
                        >
                          <IconTrash size={15} />
                        </IconButton>
                      </CellActions>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </DataTable>
      </TableWrap>
      {list.length > limit && (
        <div>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => setLimit((n) => n + PAGE)}
          >
            Показать ещё ({list.length - limit})
          </Button>
        </div>
      )}
    </>
  );
}
